import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Stack,
  Divider,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpIcon,
  EventSeat as EventSeatIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { huHU } from '@mui/x-data-grid/locales';
import weddingEventService from '../../services/weddingEventService';
import DialogHeader from '../Common/DialogHeader';
import { PREDEFINED_CATEGORIES, getCategoryColor, getCategoryLabel } from './Seating/constants';

// Hosszú cellaszöveg: ellipszis + teljes szöveg tooltipben
function TruncatedCell({ value }) {
  if (!value) return <Box component="span" sx={{ color: 'text.secondary' }}>–</Box>;
  return (
    <Tooltip title={value} placement="top-start" enterDelay={400}>
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </Box>
    </Tooltip>
  );
}

function GuestsTab({ event, onRefresh, onJumpToSeating }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Tableten is kártyalista: a táblázat ott nem fér ki értelmesen
  const useCardList = useMediaQuery(theme.breakpoints.down('md'));

  const cardSx = {
    borderRadius: '16px',
    border: `1px solid ${theme.palette.divider}`,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.secondary.light, 0.4)} 100%)`,
    boxShadow: `0 1px 3px ${alpha(theme.palette.primary.dark, 0.06)}, 0 4px 16px ${alpha(theme.palette.primary.dark, 0.04)}`,
    height: '100%',
  };
  const cardContentSx = { p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } }, textAlign: 'center' };
  const statsRowSx = {
    display: { xs: 'grid', sm: 'flex' },
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: { xs: 1.5, sm: 2 },
    mb: { xs: 2, sm: 3 },
    overflowX: { sm: 'auto' },
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    pb: 0.5,
  };
  const statItemSx = { flex: '1 1 0', minWidth: { xs: 0, sm: 130 } };

  const [guests, setGuests] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [mobileSearch, setMobileSearch] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    rsvpStatus: 'NO_RESPONSE',
    plusOne: false,
    plusOneName: '',
    dietaryRestrictions: '',
    guestCategory: '',
    notes: '',
  });

  useEffect(() => {
    fetchGuests();
    fetchTables();
  }, [event.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGuests = async () => {
    try {
      setLoading(true);
      const response = await weddingEventService.getGuests(event.id);
      if (response.success) {
        setGuests(response.data);
      }
    } catch (err) {
      console.error('Fetch guests error:', err);
      setError('Hiba történt a vendégek betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await weddingEventService.getSeatingTables(event.id);
      if (response.success) {
        setTables(response.data);
      }
    } catch (err) {
      console.error('Fetch tables error:', err);
    }
  };

  const handleAddGuest = async () => {
    try {
      await weddingEventService.addGuest(event.id, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        rsvpStatus: formData.rsvpStatus,
        plusOne: formData.plusOne,
        plusOneName: formData.plusOneName.trim() || null,
        dietaryRestrictions: formData.dietaryRestrictions.trim() || null,
        guestCategory: formData.guestCategory || null,
        notes: formData.notes.trim() || null,
      });

      setAddDialogOpen(false);
      resetForm();
      fetchGuests();
      onRefresh();
    } catch (err) {
      console.error('Add guest error:', err);
      alert('Hiba történt a vendég hozzáadása során.');
    }
  };

  const handleEditGuest = async () => {
    try {
      await weddingEventService.updateGuest(event.id, selectedGuest.id, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        rsvpStatus: formData.rsvpStatus,
        plusOne: formData.plusOne,
        plusOneName: formData.plusOne ? formData.plusOneName.trim() || null : null,
        dietaryRestrictions: formData.dietaryRestrictions.trim() || null,
        guestCategory: formData.guestCategory || null,
        notes: formData.notes.trim() || null,
      });

      setEditDialogOpen(false);
      resetForm();
      setSelectedGuest(null);
      fetchGuests();
      onRefresh();
    } catch (err) {
      console.error('Edit guest error:', err);
      alert('Hiba történt a vendég frissítése során.');
    }
  };

  // Vendéglista CSV-ként: pontosvessző + UTF-8 BOM, hogy a magyar Excel jól nyissa meg
  const handleExportGuests = () => {
    const header = ['Vezetéknév', 'Keresztnév', 'E-mail', 'Telefon', 'RSVP', 'Kísérő', 'Kísérő neve', 'Ételallergia', 'Kategória', 'Asztal', 'Megjegyzés'];
    const cell = (v) => {
      const str = v === null || v === undefined ? '' : String(v);
      return /[;"\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const rows = guests.map((g) => [
      g.lastName, g.firstName, g.email, g.phone, getRsvpLabel(g.rsvpStatus),
      g.plusOne ? 'Igen' : 'Nem', g.plusOneName, g.dietaryRestrictions,
      g.guestCategory ? getCategoryLabel(g.guestCategory) : '',
      tables.find((t) => t.id === g.tableAssignment)?.tableName, g.notes,
    ]);
    const csv = '\uFEFF' + [header, ...rows].map((r) => r.map(cell).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `vendeglista-${event?.id ?? 'esemeny'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteGuest = async (guest) => {
    if (!window.confirm(`Biztosan törölni szeretnéd: ${guest.firstName} ${guest.lastName}?`)) {
      return;
    }

    try {
      await weddingEventService.deleteGuest(event.id, guest.id);
      fetchGuests();
      onRefresh();
    } catch (err) {
      console.error('Delete guest error:', err);
      alert('Hiba történt a vendég törlése során.');
    }
  };

  const openEditDialog = (guest) => {
    setSelectedGuest(guest);
    setFormData({
      firstName: guest.firstName,
      lastName: guest.lastName,
      email: guest.email || '',
      phone: guest.phone || '',
      rsvpStatus: guest.rsvpStatus,
      plusOne: guest.plusOne,
      plusOneName: guest.plusOneName || '',
      dietaryRestrictions: guest.dietaryRestrictions || '',
      guestCategory: guest.guestCategory || '',
      notes: guest.notes || '',
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      rsvpStatus: 'NO_RESPONSE',
      plusOne: false,
      plusOneName: '',
      dietaryRestrictions: '',
      guestCategory: '',
      notes: '',
    });
  };

  const getRsvpColor = (status) => {
    const colors = {
      ATTENDING: 'success',
      NOT_ATTENDING: 'error',
      MAYBE: 'warning',
      NO_RESPONSE: 'default',
    };
    return colors[status] || 'default';
  };

  const getRsvpLabel = (status) => {
    const labels = {
      ATTENDING: 'Részt vesz',
      NOT_ATTENDING: 'Nem vesz részt',
      MAYBE: 'Talán',
      NO_RESPONSE: 'Nincs válasz',
    };
    return labels[status] || status;
  };

  const getRsvpIcon = (status) => {
    const icons = {
      ATTENDING: <CheckIcon fontSize="small" />,
      NOT_ATTENDING: <CancelIcon fontSize="small" />,
      MAYBE: <HelpIcon fontSize="small" />,
    };
    return icons[status] || null;
  };

  // Calculate stats (kísérőkkel együtt)
  const totalPlusOnes = guests.filter(g => g.plusOne).length;
  const attendingCount = guests.filter(g => g.rsvpStatus === 'ATTENDING').length;
  const attendingPlusOnes = guests.filter(g => g.rsvpStatus === 'ATTENDING' && g.plusOne).length;
  const notAttendingCount = guests.filter(g => g.rsvpStatus === 'NOT_ATTENDING').length;
  const maybeCount = guests.filter(g => g.rsvpStatus === 'MAYBE').length;
  const maybePlusOnes = guests.filter(g => g.rsvpStatus === 'MAYBE' && g.plusOne).length;
  const stats = {
    total: guests.length + totalPlusOnes,
    attending: attendingCount + attendingPlusOnes,
    notAttending: notAttendingCount,
    maybe: maybeCount + maybePlusOnes,
    noResponse: guests.filter(g => g.rsvpStatus === 'NO_RESPONSE').length,
    guestCount: guests.length,
    plusOnes: totalPlusOnes,
  };

  const mobileFilteredGuests = guests.filter(g => {
    if (!mobileSearch.trim()) return true;
    const q = mobileSearch.toLowerCase();
    return `${g.lastName} ${g.firstName}`.toLowerCase().includes(q)
      || (g.phone || '').toLowerCase().includes(q);
  });

  const columns = [
    {
      field: 'name',
      headerName: 'Név',
      flex: 1.5,
      minWidth: 200,
      valueGetter: (value, row) => `${row?.lastName || ''} ${row?.firstName || ''}`.trim(),
      renderCell: (params) => <TruncatedCell value={params.value} />,
    },
    {
      field: 'email',
      headerName: 'E-mail',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => <TruncatedCell value={params.value} />,
    },
    {
      field: 'phone',
      headerName: 'Telefon',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => <TruncatedCell value={params.value} />,
    },
    {
      field: 'rsvpStatus',
      headerName: 'RSVP',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={getRsvpLabel(params.value)}
          color={getRsvpColor(params.value)}
          size="small"
          icon={getRsvpIcon(params.value)}
        />
      ),
    },
    {
      field: 'plusOne',
      headerName: 'Kísérő',
      flex: 1,
      minWidth: 130,
      // A kísérő neve is ide kerül, hogy ne kelljen külön oszlop
      valueGetter: (value, row) => (row?.plusOne ? (row.plusOneName || 'Igen') : ''),
      renderCell: (params) => (
        params.value ? (
          params.value === 'Igen'
            ? <Chip label="Igen" size="small" color="info" variant="outlined" />
            : <TruncatedCell value={params.value} />
        ) : <TruncatedCell value="" />
      ),
    },
    {
      field: 'dietaryRestrictions',
      headerName: 'Ételallergia',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => <TruncatedCell value={params.value} />,
    },
    {
      field: 'guestCategory',
      headerName: 'Kategória',
      width: 140,
      renderCell: (params) => params.value ? (
        <Chip
          label={getCategoryLabel(params.value)}
          size="small"
          sx={{
            bgcolor: getCategoryColor(params.value) + '33',
            color: '#333',
            borderColor: getCategoryColor(params.value),
            border: '1px solid',
            fontSize: 11,
          }}
        />
      ) : '-',
    },
    {
      field: 'tableAssignment',
      headerName: 'Asztal',
      width: 130,
      renderCell: (params) => {
        const table = tables.find(t => t.id === params.value);
        if (!table) return <Typography variant="caption" color="text.secondary">–</Typography>;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Typography variant="caption" fontWeight={600}>{table.tableName}</Typography>
            {onJumpToSeating && (
              <Tooltip title="Ugrás az ülőhelyhez">
                <IconButton
                  size="small"
                  onClick={() => onJumpToSeating(params.row.id)}
                  sx={{ p: 0.25 }}
                >
                  <EventSeatIcon sx={{ fontSize: 14, color: '#2a5d32' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Műveletek',
      renderHeader: () => null,
      width: 96,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      align: 'right',
      renderCell: (params) => (
        <Box>
          <Tooltip title="Szerkesztés">
            <IconButton
              size="small"
              onClick={() => openEditDialog(params.row)}
              aria-label="Szerkesztés"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Törlés">
            <IconButton
              size="small"
              onClick={() => handleDeleteGuest(params.row)}
              color="error"
              aria-label="Törlés"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Alapból csak a lényeges oszlopok látszanak, a többi az „Oszlopok" gombbal kapcsolható be
  const defaultColumnVisibility = {
    email: false,
    phone: false,
    dietaryRestrictions: false,
    guestCategory: false,
  };

  // --- Mobil kártyalista ---
  const GuestMobileList = () => (
    <Stack spacing={1.5}>
      {mobileFilteredGuests.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Nincs találat
        </Typography>
      )}
      {mobileFilteredGuests.map((guest) => {
        const table = tables.find(t => t.id === guest.tableAssignment);
        const catColor = guest.guestCategory ? getCategoryColor(guest.guestCategory) : null;
        return (
          <Card key={guest.id} sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              {/* Sor 1: RSVP + kategória */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                <Chip
                  label={getRsvpLabel(guest.rsvpStatus)}
                  color={getRsvpColor(guest.rsvpStatus)}
                  size="small"
                  icon={getRsvpIcon(guest.rsvpStatus)}
                />
                {catColor && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: catColor, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      {getCategoryLabel(guest.guestCategory)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Sor 2: Teljes név */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                {guest.lastName} {guest.firstName}
              </Typography>

              {/* Sor 3: Telefon + kísérő */}
              {(guest.phone || guest.plusOne) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                  {guest.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">{guest.phone}</Typography>
                    </Box>
                  )}
                  {guest.plusOne && (
                    <Typography variant="caption" color="text.secondary">
                      Kísérő: {guest.plusOneName || 'névtelen'}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Sor 4: Ételallergia */}
              {guest.dietaryRestrictions && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                  {guest.dietaryRestrictions}
                </Typography>
              )}

              {/* Sor 5: Asztal */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                {table ? (
                  <>
                    <Chip label={table.tableName} size="small" variant="outlined" />
                    {onJumpToSeating && (
                      <Tooltip title="Ugrás az ülőhelyhez">
                        <IconButton size="small" onClick={() => onJumpToSeating(guest.id)} sx={{ p: 0.25 }}>
                          <EventSeatIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <Typography variant="caption" color="text.disabled">Nincs asztal</Typography>
                )}
              </Box>

              {/* Sor 6: Műveletek */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => openEditDialog(guest)}
                  sx={{ flex: 1, fontSize: '0.75rem' }}
                >
                  Szerkesztés
                </Button>
                {/* Törlés: visszafogott ikon-gomb, hogy ne versenyezzen a Szerkesztéssel */}
                <Tooltip title="Törlés">
                  <IconButton
                    onClick={() => handleDeleteGuest(guest)}
                    aria-label="Törlés"
                    sx={{ width: 40, height: 40, flexShrink: 0, color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: '8px', '&:hover': { color: 'error.main', borderColor: 'error.light' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  const dialogForm = (
    <Stack spacing={2.5} sx={{ mt: 1 }}>

      {/* ── Személyes adatok ── */}
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
          Személyes adatok
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Vezetéknév *"
              size="small"
              fullWidth
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Keresztnév *"
              size="small"
              fullWidth
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="E-mail cím"
              type="email"
              size="small"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" color="action" /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Telefonszám"
              size="small"
              fullWidth
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+36 30 123 4567"
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment> }}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* ── Visszajelzés ── */}
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
          Visszajelzés
        </Typography>
        <Stack spacing={1.5}>
          <FormControl fullWidth size="small">
            <InputLabel>RSVP Státusz</InputLabel>
            <Select
              value={formData.rsvpStatus}
              onChange={(e) => setFormData({ ...formData, rsvpStatus: e.target.value })}
              label="RSVP Státusz"
            >
              <MenuItem value="NO_RESPONSE">Nincs válasz</MenuItem>
              <MenuItem value="ATTENDING">Részt vesz</MenuItem>
              <MenuItem value="NOT_ATTENDING">Nem vesz részt</MenuItem>
              <MenuItem value="MAYBE">Talán</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.plusOne}
                onChange={(e) => setFormData({ ...formData, plusOne: e.target.checked })}
              />
            }
            label="Kísérővel érkezik"
          />
          {formData.plusOne && (
            <TextField
              label="Kísérő neve"
              size="small"
              fullWidth
              value={formData.plusOneName}
              onChange={(e) => setFormData({ ...formData, plusOneName: e.target.value })}
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" color="action" /></InputAdornment> }}
            />
          )}
        </Stack>
      </Box>

      <Divider />

      {/* ── Besorolás ── */}
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
          Besorolás
        </Typography>
        <Stack spacing={1.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Kategória</InputLabel>
            <Select
              value={formData.guestCategory}
              onChange={(e) => setFormData({ ...formData, guestCategory: e.target.value })}
              label="Kategória"
            >
              <MenuItem value="">– Nincs –</MenuItem>
              {PREDEFINED_CATEGORIES.map(c => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Ételallergia / Diétás korlátozások"
            size="small"
            fullWidth
            value={formData.dietaryRestrictions}
            onChange={(e) => setFormData({ ...formData, dietaryRestrictions: e.target.value })}
            placeholder="pl. Gluténmentes, Vegetáriánus, Laktóz érzékeny"
          />
        </Stack>
      </Box>

      <Divider />

      {/* ── Megjegyzések ── */}
      <TextField
        label="Megjegyzések"
        multiline
        rows={2}
        size="small"
        fullWidth
        value={formData.notes}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
      />

    </Stack>
  );

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats Cards */}
      <Box sx={statsRowSx}>
        {[
          { value: stats.total, label: 'Összes fő', caption: `${stats.guestCount} + ${stats.plusOnes} kísérő` },
          { value: stats.attending, label: 'Részt vesz' },
          { value: stats.notAttending, label: 'Nem vesz részt' },
          { value: stats.maybe, label: 'Talán' },
        ].map((stat) => (
          <Box key={stat.label} sx={statItemSx}>
            <Card sx={cardSx}>
              <CardContent sx={cardContentSx}>
                <Typography variant="h4" fontWeight={700} color="primary.dark" sx={{ fontSize: { xs: '1.35rem', sm: '1.75rem' } }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {stat.label}
                </Typography>
                {stat.caption && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                    {stat.caption}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Header Actions */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
        <Alert severity="info" sx={{ flex: 1 }}>
          Kezeld a vendéglistádat és kövesd nyomon az RSVP válaszokat.
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, justifyContent: { xs: 'flex-end', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportGuests}
            disabled={guests.length === 0}
            sx={{ whiteSpace: 'nowrap', minWidth: 0, fontSize: { xs: '0.8rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }}
          >
            Exportálás
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{ whiteSpace: 'nowrap', minWidth: 0, fontSize: { xs: '0.8rem', sm: '0.875rem' }, px: { xs: 1.5, sm: 2 } }}
          >
            {isMobile ? 'Hozzáadás' : 'Vendég hozzáadása'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Mobil keresőmező */}
      {useCardList && (
        <TextField
          size="small"
          fullWidth
          placeholder="Keresés (név, telefon)"
          value={mobileSearch}
          onChange={(e) => setMobileSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }}
          sx={{ mb: 2 }}
        />
      )}

      {/* Lista: mobilon/tableten kártyák, asztali nézetben DataGrid */}
      {useCardList ? (
        <GuestMobileList />
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={guests}
            columns={columns}
            initialState={{
              columns: { columnVisibilityModel: defaultColumnVisibility },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            showToolbar
            slotProps={{
              // Exportálásra a fenti „Exportálás" gomb szolgál
              toolbar: {
                csvOptions: { disableToolbarButton: true },
                printOptions: { disableToolbarButton: true },
              },
            }}
            localeText={{
              ...huHU.components.MuiDataGrid.defaultProps.localeText,
              noRowsLabel: 'Nincs még vendég',
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Box>
      )}

      {/* Add Guest Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); resetForm(); }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<AddIcon />}
            title="Vendég Hozzáadása"
            onClose={() => { setAddDialogOpen(false); resetForm(); }}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>{dialogForm}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => { setAddDialogOpen(false); resetForm(); }}>Mégse</Button>
          <Button
            onClick={handleAddGuest}
            variant="contained"
            disabled={!formData.firstName.trim() || !formData.lastName.trim()}
          >
            Hozzáadás
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Guest Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSelectedGuest(null); resetForm(); }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={<EditIcon />}
            title="Vendég Szerkesztése"
            onClose={() => { setEditDialogOpen(false); setSelectedGuest(null); resetForm(); }}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>{dialogForm}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedGuest(null); resetForm(); }}>Mégse</Button>
          <Button
            onClick={handleEditGuest}
            variant="contained"
            disabled={!formData.firstName.trim() || !formData.lastName.trim()}
          >
            Mentés
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GuestsTab;
